import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import * as Location from 'expo-location';
import BackgroundGeolocation from 'react-native-background-geolocation';
import { API_URL } from '@/lib/api';
import { checkBatteryOptimizationStatus } from './batteryOptimization';

/**
 * Lightweight diagnostics collector for OEM-tracking issues (Samsung One UI etc.).
 *
 * A ring buffer records key app-side events (permission flips, start attempts,
 * watchdog kicks, location counters), and `collectDiagnosticDump()` returns the
 * events plus the SDK's own state/log tail as a single shareable string.
 * Exposed in Settings → Permissions ("Share GPS diagnostics") so a tester can
 * paste everything to the developer without adb.
 */

const MAX_EVENTS = 120;

type DiagEvent = { t: string; tag: string; detail?: string };
const events: DiagEvent[] = [];

export const locationCounters = {
  onLocationEvents: 0,
  onLocationErrors: 0,
  lastLocationAccuracy: undefined as number | undefined,
  lastLocationAt: 0,
  rejectedStaleMarkers: 0,
  watchdogGetCurrentPositionCalls: 0,
  watchdogKicks: 0,
  healthNotificationsSent: 0,
};

function pad(n: number) { return n < 10 ? `0${n}` : String(n); }
function ts() {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function diag(tag: string, detail?: string) {
  events.push({ t: ts(), tag, detail: detail ? detail.slice(0, 300) : undefined });
  if (events.length > MAX_EVENTS) events.shift();
}

export function diagInc(counter: keyof typeof locationCounters) {
  const c = locationCounters as unknown as Record<string, number>;
  c[counter] = (c[counter] ?? 0) + 1;
}

async function safe<T>(fn: () => Promise<T>, label: string): Promise<T | `ERROR: ${string}`> {
  try {
    return await fn();
  } catch (e: any) {
    return `ERROR: ${label}: ${e?.message ?? String(e)}`;
  }
}

export async function collectDiagnosticDump(): Promise<string> {
  const lines: string[] = [];

  lines.push(`# TailTrails GPS diagnostics — ${new Date().toISOString()}`);
  lines.push(`App: v${Constants.expoConfig?.version ?? '?'} · code ${Constants.expoConfig?.android?.versionCode ?? '?'}`);
  lines.push(`Platform: ${Platform.OS} ${Platform.OS === 'android' ? Platform.Version : ''}`);
  lines.push('');

  // ── OS permission state ─────────────────────────────────────────
  const fg = await safe(() => Location.getForegroundPermissionsAsync(), 'fg-perm');
  const bg = await safe(() => Location.getBackgroundPermissionsAsync(), 'bg-perm');
  // `accuracy: 1 = precise, 2 = approximate` on Android — approximate-only
  // grants are a classic cause of "tracking runs but no usable points".
  lines.push(`Foreground loc perm: ${JSON.stringify(fg)}`);
  lines.push(`Background loc perm: ${JSON.stringify(bg)}`);

  const battery = await safe(() => checkBatteryOptimizationStatus(), 'battery');
  lines.push(`Battery optimization: ${JSON.stringify(battery)}`);
  lines.push('');

  // ── SDK state ───────────────────────────────────────────────────
  const state = await safe(() => BackgroundGeolocation.getState(), 'sdk-state');
  const provider = await safe(() => (BackgroundGeolocation as any).getProviderState?.(), 'provider-state');
  lines.push(`SDK state: ${JSON.stringify(state)}`);
  lines.push(`Provider state: ${JSON.stringify(provider)}`);
  lines.push('');

  // ── App-side counters ───────────────────────────────────────────
  lines.push('Location counters:');
  for (const [k, v] of Object.entries(locationCounters)) lines.push(`  ${k}: ${v}`);
  lines.push('');

  // ── App-side event ring ─────────────────────────────────────────
  lines.push('App events (latest last):');
  for (const e of events) lines.push(`  ${e.t} ${e.tag}${e.detail ? ` — ${e.detail}` : ''}`);
  lines.push('');

  // ── SDK log tail ────────────────────────────────────────────────
  try {
    const log: string = await BackgroundGeolocation.logger.getLog();
    // Last ~16KB is plenty to capture the latest start attempt
    lines.push('SDK log (tail):');
    lines.push(log.length > 16000 ? log.slice(-16000) : log);
  } catch (e: any) {
    lines.push(`SDK log: unavailable (${e?.message ?? e})`);
  }

  return lines.join('\n');
}

async function getAccessToken(): Promise<string | null> {
  try {
    const raw = await AsyncStorage.getItem('session');
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj?.accessToken ?? null;
  } catch {
    return null;
  }
}

/**
 * POST the diagnostic dump to tt-api's feedback endpoint (feedback_router.py
 * stores it in the `feedback` table) so failed walks land in the operator's
 * dashboard for remote troubleshooting. The endpoint takes a single opaque
 * `message` query param, bracketed with a [TRAIL-BUG] prefix for triage.
 * Fire-and-forget: caller must never await this in a user tap path.
 */
export async function reportTrackingIssue(reason: string): Promise<void> {
  try {
    const token = await getAccessToken();
    if (!token) {
      diag('remote_report_skipped', 'not signed in');
      return;
    }
    const full = await collectDiagnosticDump();
    const header = `[TRAIL-BUG] ${reason} | ${Platform.OS} ${Platform.OS === 'android' ? Platform.Version : ''} | ${Date.now()}`;
    // The endpoint reads `message` as a query param — keep it well under the
    // URL length limit; middle-snip the dump if needed.
    const body = `${header}\n${full}`;
    const message = body.length > 4000
      ? `${body.slice(0, 2400)}\n…[SNIP]…\n${body.slice(-1500)}`
      : body;
    const res = await fetch(
      `${API_URL}/feedback?message=${encodeURIComponent(message)}`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) {
      diag('remote_report_sent', `reason=${reason} status=${res.status}`);
    } else {
      diag('remote_report_failed', `reason=${reason} status=${res.status}`);
    }
  } catch (e: any) {
    diag('remote_report_error', String(e?.message ?? e));
  }
}